import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, Home, BarChart2, LogOut, Banknote, User, Layers, TrendingUp, FileText, Users, Shield, Calendar, Receipt, Wallet, Activity, Key, X, Building2, Warehouse, AlertTriangle, HelpCircle, Barcode, ShoppingBag, Calculator, Clock, Bell, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import { isCajeroUser, canManageVendedorCodigos, checkPermission } from '../utils/userRoles';

export default function Layout() {
    const { logout, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Password change modal state
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [preciosPendientesCount, setPreciosPendientesCount] = useState(0);
    const [alertCount, setAlertCount] = useState(0);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    const lastCajaCheckRef = useRef(0);
    const CAJA_CHECK_TTL_MS = 60_000;
    const PRECIOS_POLL_MS = 5 * 60 * 1000;


    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (newPassword !== confirmPassword) {
            setPasswordError('Las contraseñas no coinciden');
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/colaboradores/change-password`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ currentPassword, newPassword })
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || 'Error al cambiar contraseña');
            }

            setPasswordSuccess('Contraseña cambiada correctamente');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            
            setTimeout(() => {
                setPasswordModalOpen(false);
                setPasswordSuccess('');
            }, 2000);
        } catch (err) {
            setPasswordError(err.message);
        }
    };

    // Permission Check Helper
    const hasPermission = (permission) => {
        if (!user) return false;
        // Si no tiene array de permisos, permitir acceso (compatibilidad con admin/legacy)
        if (!user.permissions || !Array.isArray(user.permissions)) {
            console.warn(`⚠️ User ${user.email} has no permissions array, allowing access (legacy mode)`);
            return true;
        }
        const hasPerm = user.permissions.includes(permission);
        if (!hasPerm && process.env.NODE_ENV === 'development') {
            console.log(`🚫 Permission denied: ${permission} for user ${user.email}`);
        }
        return hasPerm;
    };

    const isActive = (path) => location.pathname === path ? 'active' : '';

    const initials = (user?.name || 'U')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();

    const todayLabel = new Date().toLocaleDateString('es-CL', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'America/Santiago',
    });

    const getPageTitle = () => {
        switch (location.pathname) {
            case '/': return 'Panel de Control';
            case '/users': return 'Gestión de Usuarios';
            case '/proveedores': return 'Gestión de Proveedores';
            case '/promociones': return 'Promociones y Packs';
            case '/products': return 'Gestión de Inventario';
            case '/categories': return 'Gestión de Categorías';
            case '/pos': return 'Punto de Venta';
            case '/reports': return 'Reportes';
            case '/compras': return 'Compras y Proveedores';
            case '/precios-pendientes': return 'Precios pendientes';
            case '/preventas': return 'Gestión de Preventas';
            case '/ordenes-online': return 'Ventas Online';
            case '/vendedores-barcodes': return 'Códigos de Barras - Vendedores';
            case '/ventas': return 'Historial de Ventas';
            case '/colaboradores': return 'Gestión de Colaboradores';
            case '/liquidaciones': return 'Liquidaciones de Sueldo';
            case '/roles': return 'Roles y Permisos';
            case '/agenda': return 'Agenda de Turnos';
            case '/flujo-caja': return 'Flujo de Caja';
            case '/usuarios-activos': return 'Usuarios Activos';
            case '/sucursales': return 'Gestión de Sucursales';
            case '/cajas': return 'Gestión de Cajas';
            case '/bodegas': return 'Gestión de Bodegas';
            case '/stock': return 'Gestión de Stock';
            case '/vencimientos': return 'Control de Vencimientos';
            case '/ayuda': return 'Centro de Ayuda';
            default: return 'ERP Minimarket';
        }
    };

    useEffect(() => {
        if (!user || !isCajeroUser(user)) return;
        if (location.pathname === '/pos') return;

        const now = Date.now();
        if (now - lastCajaCheckRef.current < CAJA_CHECK_TTL_MS) return;
        lastCajaCheckRef.current = now;

        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await fetch(`${API_URL}/api/cajas/status`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (cancelled) return;
                if (res.status === 401) {
                    logout();
                    return;
                }
                if (res.ok) {
                    const data = await res.json();
                    if (!data.isOpen) {
                        navigate('/pos?openCaja=1', { replace: true });
                    }
                } else {
                    navigate('/pos?openCaja=1', { replace: true });
                }
            } catch {
                if (!cancelled) navigate('/pos?openCaja=1', { replace: true });
            }
        })();
        return () => { cancelled = true; };
    }, [user, location.pathname, navigate, logout]);

    useEffect(() => {
        if (!user || !checkPermission(user, 'precios_pendientes_ver')) return;
        let cancelled = false;
        const loadCount = async () => {
            if (document.visibilityState === 'hidden') return;
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await fetch(`${API_URL}/api/precios-pendientes/count`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) setPreciosPendientesCount(Number(data.pendientes) || 0);
            } catch { /* ignore */ }
        };
        const onVisible = () => {
            if (document.visibilityState === 'visible') loadCount();
        };
        loadCount();
        const t = setInterval(loadCount, PRECIOS_POLL_MS);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            cancelled = true;
            clearInterval(t);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [user]);

    useEffect(() => {
        if (!user) return undefined;
        const perms = user.permissions;
        const allowed = !Array.isArray(perms)
            || perms.includes('reportes_ver')
            || perms.includes('compras_ver');
        if (!allowed) return undefined;
        let cancelled = false;
        const token = localStorage.getItem('token');
        fetch(`${API_URL}/api/reports/alerts`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => {
                if (!cancelled) setAlertCount(Array.isArray(data) ? data.length : 0);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user]);

    useEffect(() => {
        const onDoc = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    return (
        <div className="layout">
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <span className="sidebar-mark" aria-hidden>E</span>
                    <span>ERP Minimarket</span>
                </div>

                <nav className="sidebar-nav">
                    {/* Public / Common */}
                    <Link to="/" className={`nav-link ${isActive('/')}`}>
                        <Home size={20} /> Inicio
                    </Link>
                    <Link to="/ayuda" className={`nav-link ${isActive('/ayuda')}`}>
                        <HelpCircle size={20} /> Ayuda
                    </Link>

                    {/* Inventory Group */}
                    {hasPermission('productos_ver') && (
                        <Link to="/products" className={`nav-link ${isActive('/products')}`}>
                            <Package size={20} /> Productos
                        </Link>
                    )}
                    {hasPermission('categorias_ver') && (
                        <Link to="/categories" className={`nav-link ${isActive('/categories')}`}>
                            <Layers size={20} /> Categorías
                        </Link>
                    )}

                    {/* POS & Sales */}
                    {hasPermission('pos_ver') && (
                        <Link to="/pos" className={`nav-link ${isActive('/pos')}`}>
                            <ShoppingCart size={20} /> Punto de Venta
                        </Link>
                    )}
                    {hasPermission('reportes_ver') && (
                        <Link to="/reports" className={`nav-link ${isActive('/reports')}`}>
                            <BarChart2 size={20} /> Reportes
                        </Link>
                    )}

                    {/* Operations */}
                    {hasPermission('inventarios_ver') && (
                        <Link to="/inventarios" className={`nav-link ${isActive('/inventarios')}`}>
                            <Layers size={20} /> Inventarios
                        </Link>
                    )}
                    {hasPermission('stock_ver') && (
                        <Link to="/stock" className={`nav-link ${isActive('/stock')}`}>
                            <Package size={20} /> Stock
                        </Link>
                    )}
                    {hasPermission('compras_ver') && (
                        <Link to="/vencimientos" className={`nav-link ${isActive('/vencimientos')}`}>
                            <AlertTriangle size={20} /> Vencimientos
                        </Link>
                    )}

                    {/* Admin / People */}
                    {hasPermission('usuarios_ver') && (
                        <Link to="/users" className={`nav-link ${isActive('/users')}`}>
                            <User size={20} /> Usuarios
                        </Link>
                    )}

                    {/* Purchases */}
                    {hasPermission('compras_ver') && (
                        <Link to="/compras" className={`nav-link ${isActive('/compras')}`}>
                            <Package size={20} /> Compras
                        </Link>
                    )}
                    {checkPermission(user, 'precios_pendientes_ver') && (
                        <Link to="/precios-pendientes" className={`nav-link ${isActive('/precios-pendientes')}`}>
                            <Clock size={20} /> Precios pendientes
                            {preciosPendientesCount > 0 && (
                                <span className="ml-auto bg-amber-500 text-white text-[10px] font-bold min-w-[1.25rem] h-5 px-1.5 rounded-full inline-flex items-center justify-center">
                                    {preciosPendientesCount}
                                </span>
                            )}
                        </Link>
                    )}
                    {hasPermission('proveedores_ver') && (
                        <Link to="/proveedores" className={`nav-link ${isActive('/proveedores')}`}>
                            <User size={20} /> Proveedores
                        </Link>
                    )}

                    {/* Marketing */}
                    {hasPermission('promociones_ver') && (
                        <Link to="/promociones" className={`nav-link ${isActive('/promociones')}`}>
                            <TrendingUp size={20} /> Promociones
                        </Link>
                    )}

                    {/* Sales History/Pre-sales */}
                    {hasPermission('preventas_ver') && (
                        <Link to="/preventas" className={`nav-link ${isActive('/preventas')}`}>
                            <FileText size={20} /> Preventas
                        </Link>
                    )}
                    {hasPermission('preventas_ver') && (
                        <Link to="/ordenes-online" className={`nav-link ${isActive('/ordenes-online')}`}>
                            <ShoppingBag size={20} /> Ventas Online
                        </Link>
                    )}
                    {canManageVendedorCodigos(user) && (
                        <Link to="/vendedores-barcodes" className={`nav-link ${isActive('/vendedores-barcodes')}`}>
                            <Barcode size={20} /> Códigos Vendedores
                        </Link>
                    )}
                    {hasPermission('ventas_ver') && (
                        <Link to="/ventas" className={`nav-link ${isActive('/ventas')}`}>
                            <Receipt size={20} /> Ventas
                        </Link>
                    )}

                    <div className="nav-separator">
                        <span className="nav-separator-label">Finanzas</span>
                    </div>

                    {hasPermission('reportes_ver') && (
                        <Link to="/flujo-caja" className={`nav-link ${isActive('/flujo-caja')}`}>
                            <Wallet size={20} /> Flujo de Caja
                        </Link>
                    )}

                    <div className="nav-separator">
                        <span className="nav-separator-label">Recursos Humanos</span>
                    </div>

                    {hasPermission('colaboradores_ver') && (
                        <Link to="/colaboradores" className={`nav-link ${isActive('/colaboradores')}`}>
                            <Users size={20} /> Colaboradores
                        </Link>
                    )}
                    {hasPermission('roles_ver') && (
                        <Link to="/roles" className={`nav-link ${isActive('/roles')}`}>
                            <Shield size={20} /> Roles
                        </Link>
                    )}
                    {hasPermission('turnos_ver') && (
                        <Link to="/agenda" className={`nav-link ${isActive('/agenda')}`}>
                            <Calendar size={20} /> Agenda Turnos
                        </Link>
                    )}
                    {hasPermission('liquidaciones_ver') && (
                        <Link to="/liquidaciones" className={`nav-link ${isActive('/liquidaciones')}`}>
                            <Calculator size={20} /> Liquidaciones
                        </Link>
                    )}

                    <div className="nav-separator">
                        <span className="nav-separator-label">Administración</span>
                    </div>

                    {hasPermission('usuarios_ver') && (
                        <Link to="/usuarios-activos" className={`nav-link ${isActive('/usuarios-activos')}`}>
                            <Activity size={20} /> Usuarios Activos
                        </Link>
                    )}
                    {hasPermission('sucursales_ver') && (
                        <Link to="/sucursales" className={`nav-link ${isActive('/sucursales')}`}>
                            <Building2 size={20} /> Sucursales
                        </Link>
                    )}
                    {hasPermission('cajas_ver') && (
                        <Link to="/cajas" className={`nav-link ${isActive('/cajas')}`}>
                            <Banknote size={20} /> Cajas
                        </Link>
                    )}
                    {hasPermission('bodegas_ver') && (
                        <Link to="/bodegas" className={`nav-link ${isActive('/bodegas')}`}>
                            <Warehouse size={20} /> Bodegas
                        </Link>
                    )}
                </nav>

                <div className="user-menu">
                    <div className="user-avatar" aria-hidden>{initials}</div>
                    <div className="user-info">
                        <div className="user-name">{user?.name || 'Usuario'}</div>
                        <div className="user-info-label">Sesión activa</div>
                    </div>
                    <button
                        onClick={() => setPasswordModalOpen(true)}
                        className="logout-btn logout-btn-accent"
                        title="Cambiar Contraseña"
                        aria-label="Cambiar contraseña"
                    >
                        <Key size={20} />
                    </button>
                    <button
                        onClick={handleLogout}
                        className="logout-btn"
                        title="Cerrar Sesión"
                        aria-label="Cerrar sesión"
                    >
                        <LogOut size={20} />
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="main-wrapper">
                <header className="topbar">
                    <div className="page-title">{getPageTitle()}</div>
                    <div className="topbar-actions">
                        <span className="topbar-date">
                            <Calendar size={15} aria-hidden />
                            {todayLabel}
                        </span>
                        <button
                            type="button"
                            className="topbar-bell"
                            title="Alertas de vencimiento"
                            aria-label="Alertas de vencimiento"
                            onClick={() => navigate('/vencimientos')}
                        >
                            <Bell size={18} />
                            {alertCount > 0 && <i className="topbar-bell-dot" />}
                        </button>
                        <div className="topbar-user" ref={userMenuRef}>
                            <button
                                type="button"
                                className="topbar-user-btn"
                                onClick={() => setUserMenuOpen((open) => !open)}
                                aria-expanded={userMenuOpen}
                            >
                                <span className="topbar-avatar">{initials}</span>
                                <span className="topbar-user-name">{user?.name || 'Usuario'}</span>
                                <ChevronDown size={16} />
                            </button>
                            {userMenuOpen && (
                                <div className="topbar-user-menu">
                                    <button type="button" onClick={() => { setUserMenuOpen(false); setPasswordModalOpen(true); }}>
                                        Cambiar contraseña
                                    </button>
                                    <button type="button" onClick={handleLogout}>Cerrar sesión</button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main
                    className={
                        location.pathname === '/pos'
                            ? 'content-area content-area--pos'
                            : 'content-area'
                    }
                >
                    <Outlet />
                </main>
            </div>

            {/* Password Change Modal */}
            {passwordModalOpen && (
                <div className="modal-password-overlay">
                    <div className="modal-password-content">
                        <div className="modal-password-header">
                            <h2 className="modal-password-title">
                                <Key size={24} aria-hidden /> Cambiar Contraseña
                            </h2>
                            <button
                                type="button"
                                onClick={() => { setPasswordModalOpen(false); setPasswordError(''); setPasswordSuccess(''); }}
                                className="modal-password-close"
                                aria-label="Cerrar modal"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {passwordError && (
                            <div className="modal-alert-error" role="alert">{passwordError}</div>
                        )}

                        {passwordSuccess && (
                            <div className="modal-alert-success" role="status">{passwordSuccess}</div>
                        )}

                        <form onSubmit={handleChangePassword}>
                            <div className="modal-input-group">
                                <label htmlFor="current-password">Contraseña Actual</label>
                                <input
                                    id="current-password"
                                    type={showPasswords ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                            </div>

                            <div className="modal-input-group">
                                <label htmlFor="new-password">Nueva Contraseña</label>
                                <input
                                    id="new-password"
                                    type={showPasswords ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    placeholder="Mínimo 6 caracteres"
                                    autoComplete="new-password"
                                />
                            </div>

                            <div className="modal-input-group">
                                <label htmlFor="confirm-password">Confirmar Nueva Contraseña</label>
                                <input
                                    id="confirm-password"
                                    type={showPasswords ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                            </div>

                            <label className="modal-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={showPasswords}
                                    onChange={e => setShowPasswords(e.target.checked)}
                                />
                                Mostrar contraseñas
                            </label>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    onClick={() => { setPasswordModalOpen(false); setPasswordError(''); setPasswordSuccess(''); }}
                                    className="modal-btn-secondary"
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="modal-btn-primary">
                                    Cambiar Contraseña
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
